import { pickAppCopy, readStoredLocale, resolveLocale, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { toastCopyEn } from "@patient-watch/i18n/webapp/en-GB/toastCopy";
import { toastCopyDe } from "@patient-watch/i18n/webapp/de-DE/toastCopy";
import { toastCopyDa } from "@patient-watch/i18n/webapp/da-DK/toastCopy";
import { toastCopyEnUs } from "@patient-watch/i18n/webapp/en-US/toastCopy";
import { toastCopyFr } from "@patient-watch/i18n/webapp/fr-FR/toastCopy";
import { toastCopyIt } from "@patient-watch/i18n/webapp/it-IT/toastCopy";
import { toastCopyEs } from "@patient-watch/i18n/webapp/es-ES/toastCopy";

export interface ToastCopy {
    // Generic
    saved: string;
    savedForNow: string;
    savedSuccessfully: string;
    errorSaving: string;
    errorSavingTracker: string;
    updateFailed: string;
    errorUpdating: string;
    errorCreating: string;
    errorCreatingInput: string;
    errorCreatingDiaryLog: string;
    errorCreatingEvent: string;
    errorUpdatingEvent: string;
    errorUpdatingProfile: string;
    errorWhileUploading: string;
    refreshAndRetry: string;
    noDataReturned: string;
    settingsSaved: string;
    copiedToClipboard: string;
    linkCopiedToClipboard: string;
    pleaseSelectFile: string;
    pasteImageFromClipboard: string;

    // Auth
    loggedOutSuccess: string;
    loggingIn: string;
    errorLoggingIn: string;
    loginSuccessful: string;
    signUpSuccessful: string;
    transferRequested: string;
    organisationAdded: string;
    organisationAlreadyAdded: string;
    deletionRequestSent: string;
    userAuthenticated: string;
    codeIncorrect: string;
    codeCorrect: string;
    errorSigningIn: string;
    otpFailedToSend: string;
    passwordUpdatedSuccess: string;
    emailOrPasswordIncorrect: string;
    phoneNumberConfirmed: string;
    errorConfirmingPhoneNumber: string;

    // Profile
    profileUpdatedSuccess: string;
    professionalDetailsSaved: string;
    failedUpdateProfessionalDetails: string;

    // Templates
    templateCreatedSuccess: string;
    templatesCreatedSuccess: (count: number) => string;
    failedCreateTemplate: string;
    templateUpdatedSuccess: string;
    failedUpdateTemplate: string;
    templateDeletedSuccess: string;
    failedDeleteTemplate: string;
    templateDuplicatedSuccess: string;
    failedDuplicateTemplate: string;
    templateSharedSuccess: string;
    errorSharingTemplate: (message: string) => string;

    // Analytics views
    analyticsViewSharedSuccess: string;
    errorSharingAnalyticsView: (message: string) => string;
    analyticsViewUnsharedSuccess: string;
    errorUnsharingAnalyticsView: (message: string) => string;

    // Trackers & entries
    trackerSaved: string;
    entryUpdatedSuccess: string;
    entrySaved: string;
    entryDeleted: string;
    entryTimingUpdated: string;
    scheduleUpdated: string;
    entryRemovedFromSchedule: string;
    entryAssignedToScheduleStep: string;
    stepAlreadyOccupied: string;

    // Diaries & labels
    diaryUpdatedSuccess: string;
    diaryInputsAddedSuccess: string;
    diaryDiscardedSuccess: string;
    diaryReinstatedSuccess: string;
    diaryDischargedSuccess: string;
    diaryEnded: string;
    labelCreated: string;
    labelUpdated: string;
    labelDeleted: string;
    labelsUpdated: string;
    tableSettingsSaved: string;
    detailsSaved: string;
    hospitalNumberRequired: string;
    couldNotSaveHospitalNumber: string;

    // Shared attributes & access
    sharedAttributesUpdated: string;
    errorUpdatingSharedAttributes: string;
    sharedAttributesCreated: string;
    errorCreatingSharedAttributes: string;
    diaryAccessSaved: string;
    failedSaveDiaryAccess: string;
    joinedDateGroupSuccess: string;
    failedJoinDateGroup: (message: string) => string;
    questionnaireStopped: string;
    errorStoppingQuestionnaire: string;
    consentAddedToDiary: string;
    errorAddingConsentToDiary: string;

    // Events & procedures
    eventSaved: string;
    eventUpdated: string;
    eventDeletedSuccess: string;
    eventTrackerLinked: string;
    errorLinkingEventTracker: (message: string) => string;
    surgerySaved: string;
    errorCreatingSurgery: string;
    surgeryOptionUpdated: string;
    errorUpdatingSurgeryOption: string;
    newSurgeryOptionSaved: string;
    consultationSaved: string;
    errorCreatingConsultation: string;
    errorUpdatingConsultation: string;
    imagingRequestSaved: string;
    errorCreatingImagingRequest: string;

    // Injection & shockwave
    injectionDetailsUpdated: string;
    errorUpdatingInjectionDetails: string;
    injectionDiaryDefaultSaved: string;
    errorSavingInjectionDiaryDefault: string;
    diaryInjectionAdded: string;
    errorCreatingDiaryInjection: string;
    injectionContentUpdated: string;
    failedUpdateInjectionContent: string;
    injectionContentDeleted: string;
    failedDeleteInjectionContent: string;
    publicStatusUpdated: string;
    failedUpdatePublicStatus: string;
    injectionLocationUpdated: string;
    failedUpdateInjectionLocation: string;
    newInjectionLocationCreated: string;
    failedCreateInjectionLocation: string;
    newInjectionContentsCreated: string;
    failedCreateInjectionContents: string;
    failedSaveInjectionDetails: string;
    diaryShockwaveAdded: string;
    errorCreatingDiaryShockwave: string;
    diaryShockwaveSaved: string;
    shockwaveDetailsUpdated: string;
    errorUpdatingShockwaveDetails: string;
    shockwaveDiaryDefaultSaved: string;
    errorSavingShockwaveDiaryDefault: string;
    shockwaveDetailsRemoved: string;
    failedSaveShockwaveDetails: string;

    // Schedules & forms
    scheduleUpdatedSuccess: string;
    failedUpdateSchedule: string;
    scheduleDraftCreated: string;
    failedCreateScheduleFromDraft: string;
    failedUpdatePublishStatus: string;
    formUpdated: string;
    failedUpdateForm: string;
    formDraftCreated: string;
    failedCreateFormFromDraft: string;
    pageSaved: string;
    failedSavePage: string;
    pageDeleted: string;
    failedDeletePage: string;
    questionSaved: string;
    failedSaveQuestion: string;
    questionDeleted: string;
    failedDeleteQuestion: string;

    // Patients & email
    patientAddedSuccess: string;
    emailWordsUpdatedSuccess: string;
    failedUpdateEmailWords: string;
    emailSentSuccess: string;
    diaryAlreadyRead: string;
    noLastReadAction: string;
    actionComplete: (actionName: string) => string;

    // Foot watch & QIP
    footLogCreatedSuccess: string;
    imageUploadedSuccess: string;
    imageLabelCreatedSuccess: string;
    qipPermissionsUpdated: string;
    qipPermissionsUpdateFailed: string;
    riskAssessmentUpdateFailed: string;
    footCommentSubmitted: (comment: string) => string;

    // QR & invite
    qrCodeNotReady: string;
    qrCodeCopiedToClipboard: string;
    couldNotCopyQrImage: string;
    inviteSent: string;

    // Organisation & admin
    organisationRoleAddedSuccess: string;
    errorAddingOrganisationRole: (message: string) => string;
    organisationRoleUpdatedSuccess: string;
    errorUpdatingOrganisationRole: (message: string) => string;
    organisationRoleDeletedSuccess: string;
    errorDeletingOrganisationRole: (message: string) => string;
    secretaryAddedToOrganisation: string;
    errorAddingSecretaryToOrganisation: (message: string) => string;
    defaultSecretarySet: string;
    errorSettingDefaultSecretary: (message: string) => string;
    defaultSecretaryCleared: string;
    errorClearingDefaultSecretary: (message: string) => string;
    secretaryRemovedFromOrganisation: string;
    errorRemovingSecretaryFromOrganisation: (message: string) => string;
    newOrganisationSaved: string;
    organizationCreatedSuccess: string;
    audiencesUpdatedSuccess: string;
    errorUpdatingAudiences: string;
    labelAccessUpdated: string;
    accessUpdated: string;
    photoUpdatedSuccess: string;
    failedUpdatePhoto: string;
    photoDeletedSuccess: string;
    failedDeletePhoto: string;
    logoUpdatedSuccess: string;
    failedUpdateLogo: string;
    logoDeletedSuccess: string;
    failedDeleteLogo: string;
    smsDisabledInKnock: string;
    failedUpdateKnockPreferences: string;
    magicLinkGenerated: string;
    errorImpersonatingUser: string;
    fieldUpdatedSuccess: (field: string) => string;
    lockUnlockReasonRequired: (action: "lock" | "unlock") => string;
    lockUnlockSuccess: (action: "lock" | "unlock", count: number) => string;
    errorResolvedSuccess: string;
    failedResolveError: (message: string) => string;
    healthCheckCompleted: (message: string) => string;
    healthCheckFailed: (message: string) => string;
    expectedCountUpdatedSuccess: string;
    failedUpdateExpectedCount: (message: string) => string;
    fixedRecords: (fixed: number, failed: number) => string;
    failedFixSharedAttributes: (message: string) => string;
    failedAssignRole: (message: string) => string;
    failedLoadMessages: string;
    // Additional auth & common
    loggedInSuccess: string;
    unknownLoginError: string;
    userDeleted: string;
    passwordSetSuccess: string;
    emailVerificationSent: string;
    verificationCodeSent: string;
    verificationCodeSentEmail: string;
    failedSendVerificationCode: string;
    emailVerificationFailed: string;
    emailVerified: string;
    emailVerifiedShort: string;
    invalidVerificationCode: string;
    emailSetSuccess: string;
    emailSavedToProfileToast: string;
    emailUpdatedSuccessToast: string;
    superUserStatusUpdated: string;
    userInvited: string;
    profileAttributeLocked: string;
    addressAlreadyInUse: string;
    mustBeSignedIn: string;
    contactPersonRequired: string;
    somethingWentWrong: string;
    resultsSentToEmail: string;
    resultsEmailSendFailed: string;
    fixHighlightedFieldsBeforeInvite: string;
    // PDF
    pdfDownloaded: string;
    pdfDownloadFailed: string;
    pdfGenerationFailed: string;
    // Questionnaires & trackers
    questionnaireResumed: string;
    errorResumingQuestionnaire: string;
    trackerDeletedSuccess: string;
    diaryCreatedSuccess: string;
    diaryDeletedSuccess: string;
    templateSavedSuccess: string;
    failedSaveTemplate: string;
    diaryAccessRemoved: string;
    failedRemoveDiaryAccess: string;
    entryRemindersTriggered: string;
    correctionSaved: string;
    correctionReasonRequired: string;
    errorAddingPatient: string;
    invalidDate: string;
    failedCreateDiary: (message: string) => string;
    failedUpdateEntry: (message: string) => string;
    failedDeleteSchedule: (message: string) => string;
    failedCopySchedule: (message: string) => string;
    errorAddingUserToOrganisation: (message: string) => string;
    emailRequiredRefresh: string;
    templateStillLoading: string;
    phoneUpdatedSuccess: string;
    failedUpdatePhone: string;
    completeRequiredFields: (fieldsList: string) => string;
    missingEntry: string;
    formSubmissionError: (message: string) => string;
    organisationDeleted: string;
    noRowsToExport: string;
    exportedChangeEventsFromPage: (count: number) => string;
    noChangeEventsMatchFilters: string;
    exportedChangeEvents: (count: number) => string;
    mustSignInToExportSnapshot: string;
    noDiariesToExport: string;
    chooseSnapshotDateTime: string;
    snapshotExported: (count: number, dateTime: string) => string;
    snapshotExportFailed: string;
    exportFailed: string;
    exportedChangeEventsTruncated: (exported: number, total: number) => string;
    printing: string;
    failedGeneratePrintUrl: string;
    reportPrintQueued: string;
    reportPrintRendering: string;
    reportPrintRefreshing: string;
    reportPrintPreparing: string;
    reportPrintTimeout: string;
    unknownError: string;
    failedCreateOrganisation: string;
    correctionFailed: string;
    couldNotSaveProcedureDate: string;
    couldNotSave: string;
    invalidFieldSelection: string;
    completeAllRequiredFieldsBeforeSaving: string;
    detailsAlreadySaved: string;
    unableToSaveInjectionDetails: string;
    unableToSaveShockwaveDetails: string;
    invitationLinkExpired: string;
    invitationLinkInvalid: string;
    confirmAccountCouldNotTextTryEmail: string;
    confirmAccountNoAccountForDetails: string;
    smsVerificationUnavailableForNumber: string;
    smsVerificationTextFailedSignup: string;
    smsSignInNotEnabledForProject: string;
    smsCouldNotSendCheckNumber: string;
    smsLoginTextFailedOtpDisabled: string;
    smsLoginTextFailedAccountFound: string;
    clinicianUpdateFailedDescription: string;
    lockUnlockPartialFailure: (
        action: "lock" | "unlock",
        succeeded: number,
        failed: number,
        failedNames: string,
        firstError: string
    ) => string;
    rolesAssignedToUsers: (role: string, assigned: number, failed: number) => string;
    pdfDownloadFailedDescription: string;
    pdfGenerationFailedDescription: string;
    pdfDownloadSuccessDescription: string;
}

const toastCopy: Record<SupportedLocale, ToastCopy> = {
    "en-GB": toastCopyEn,
    "en-US": toastCopyEnUs,
    "de-DE": toastCopyDe,
    "da-DK": toastCopyDa,
    "fr-FR": toastCopyFr,
    "it-IT": toastCopyIt,
    "es-ES": toastCopyEs,
};

export function getToastCopy(locale?: SupportedLocale | null): ToastCopy {
    const resolved =
        locale ??
        resolveLocale({
            storedLocale: readStoredLocale(),
            navigatorLanguage: typeof navigator !== "undefined" ? navigator.language : null,
        });
    return pickAppCopy(toastCopy, resolved);
}

export function useToastCopy(): ToastCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(toastCopy, locale);
}
